import { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useTheme } from '../contexts/ThemeContext';
import { useAuth } from '../hooks/useAuth';
import Card from '../components/ui/Card';
import Button from '../components/ui/Button';

const MAX_ATTEMPTS = 20;
const POLL_INTERVAL_MS = 3000;

// Fire Google Ads conversion event
function fireConversionEvent() {
  if (typeof (window as any).gtag === 'function') {
    (window as any).gtag('event', 'conversion', {
      send_to: 'AW-18111186767/NswvCN-Y0aAcEM-Oi7xD',
      value: 59.88,
      currency: 'AUD',
    });
  }
}

export default function PaymentSuccess() {
  const { theme } = useTheme();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const sessionId = searchParams.get('session_id');
  const { userId } = useAuth();

  const [ready, setReady] = useState(false);
  const [statusMessage, setStatusMessage] = useState('Confirming your subscription...');
  const [planName, setPlanName] = useState('Sky One');
  const [storageGB, setStorageGB] = useState(50);

  useEffect(() => {
    if (!userId) return;

    let attempt = 0;
    let cancelled = false;

    const poll = async () => {
      while (attempt < MAX_ATTEMPTS && !cancelled) {
        attempt++;

        try {
          const url = `${process.env.REACT_APP_GET_USER_STATUS_URL}&userId=${encodeURIComponent(userId)}`;
          const response = await fetch(url);
          const data = await response.json();

          if (['trialing', 'active'].includes(data.status)) {
            if (!cancelled) {
              // Read plan details from response
              if (data.quota) {
                setPlanName(data.quota.planName || 'Sky One');
                setStorageGB(data.quota.storageGB || 50);
              }
              sessionStorage.setItem('clearSubscriptionCache', 'true');
              setStatusMessage('Subscription confirmed! ✓');
              setReady(true);
              fireConversionEvent();
            }
            return;
          }
        } catch (err) {
          console.warn('Poll attempt failed:', err);
        }

        await new Promise((res) => setTimeout(res, POLL_INTERVAL_MS));
      }

      if (!cancelled) {
        setStatusMessage('Taking longer than expected — you can proceed.');
        setReady(true);
        fireConversionEvent();
      }
    };

    poll();

    return () => { cancelled = true; };
  }, [userId]);

  const handleGoToDashboard = () => {
    sessionStorage.setItem('clearSubscriptionCache', 'true');
    navigate('/home');
  };

  return (
    <div style={{
      minHeight: '100vh',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      background: theme.colors.bgPrimary,
      padding: '20px',
    }}>
      <Card>
        <div style={{
          padding: '60px 40px',
          textAlign: 'center',
          maxWidth: '500px',
        }}>
          {/* Success Icon */}
          <div style={{
            width: '80px',
            height: '80px',
            borderRadius: '50%',
            background: 'linear-gradient(135deg, #10B981 0%, #059669 100%)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            margin: '0 auto 32px',
            fontSize: '40px',
          }}>
            ✓
          </div>

          <h1 style={{
            fontSize: '32px',
            fontWeight: 700,
            color: theme.colors.textPrimary,
            marginBottom: '16px',
          }}>
            Payment Successful!
          </h1>

          <p style={{
            fontSize: '18px',
            color: theme.colors.textSecondary,
            marginBottom: '32px',
            lineHeight: 1.6,
          }}>
            Thank you for subscribing to <strong style={{ color: theme.colors.brand }}>{planName}</strong>! Your account has been upgraded and you now have access to {storageGB} GB of storage.
          </p>

          {/* Subscription confirmation status */}
          <div style={{
            background: ready ? `${theme.colors.brand}15` : `${theme.colors.bgSecondary}`,
            border: `1px solid ${ready ? theme.colors.brand : theme.colors.border}`,
            borderRadius: '8px',
            padding: '16px',
            marginBottom: '24px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: '10px',
            transition: 'all 0.3s ease',
          }}>
            {!ready && (
              <div style={{
                width: '16px',
                height: '16px',
                border: `2px solid ${theme.colors.brand}`,
                borderTop: '2px solid transparent',
                borderRadius: '50%',
                animation: 'spin 0.8s linear infinite',
                flexShrink: 0,
              }} />
            )}
            <span style={{
              color: ready ? theme.colors.brand : theme.colors.textSecondary,
              fontSize: '14px',
              fontWeight: 500,
            }}>
              {statusMessage}
            </span>
          </div>

          {/* Features list */}
          <div style={{
            background: `${theme.colors.brand}10`,
            borderRadius: '8px',
            padding: '20px',
            marginBottom: '32px',
          }}>
            <h3 style={{
              fontSize: '16px',
              fontWeight: 600,
              color: theme.colors.textPrimary,
              marginBottom: '12px',
            }}>
              What's included in {planName}:
            </h3>
            <ul style={{
              listStyle: 'none',
              padding: 0,
              margin: 0,
              display: 'flex',
              flexDirection: 'column',
              gap: '8px',
              textAlign: 'left',
            }}>
              {[
                `${storageGB} GB secure storage`,
                'Unlimited uploads',
                'All premium features',
                'Email support',
              ].map((item) => (
                <li key={item} style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <span style={{ color: theme.colors.brand }}>✓</span>
                  <span style={{ color: theme.colors.textSecondary }}>{item}</span>
                </li>
              ))}
            </ul>
          </div>

          <Button
            variant="primary"
            onClick={handleGoToDashboard}
            disabled={!ready}
            style={{
              width: '100%',
              marginBottom: '12px',
              opacity: ready ? 1 : 0.5,
              cursor: ready ? 'pointer' : 'not-allowed',
            }}
          >
            {ready ? 'Go to Dashboard →' : 'Please wait...'}
          </Button>

          <Button
            variant="secondary"
            onClick={() => navigate('/settings')}
            disabled={!ready}
            style={{
              width: '100%',
              opacity: ready ? 1 : 0.5,
              cursor: ready ? 'pointer' : 'not-allowed',
            }}
          >
            View Subscription Details
          </Button>

          {sessionId && (
            <p style={{
              fontSize: '12px',
              color: theme.colors.textTertiary,
              marginTop: '24px',
            }}>
              Session ID: {sessionId}
            </p>
          )}
        </div>
      </Card>

      {/* Spinner animation */}
      <style>{`
        @keyframes spin {
          to { transform: rotate(360deg); }
        }
      `}</style>
    </div>
  );
}
